<template>
  <div class="tw:flex tw:flex-col tw:h-full">
    <v-alert border="top" color="error-lighten-2" v-if="!loadingAccount && errorAccount">{{ errorAccount }}</v-alert>

    <div class="progress text-center" v-if="loadingAccount">
      <v-progress-circular :size="70" color="primary" indeterminate></v-progress-circular>
    </div>

    <div v-else id="account-header" class="inner">
      <div class="tw:flex tw:place-content-between tw:gap-5 tw:items-center">
        <div>
          <div class="tw:flex tw:content-between tw:gap-3 tw:items-center tw:mb-1">
            <h1 class="tw:truncate tw:max-w-lg">{{ account.displayName }}</h1>

            <v-chip class="tw:uppercase tw:tracking-wide tw:font-bold" color="blue-lighten-2" variant="outlined" dense small
                    v-if="account.accountStatus === 'P'" data-test="prospect-chip">
              prospect
            </v-chip>
            <FavoriteIndicator v-if="!isAClientAccount" v-model="account.favorite" @update:model-value="toggleFavorite(account)" :loading="favoriteProgress" class="tw:text-center text-primary" />
            <div class="tw:tooltip tw:tooltip-bottom tw:flex tw:items-center" data-tip="Edit Account">
              <Pencil v-if="!isAClientAccount" :size="16" class="tw:stroke-primary tw:cursor-pointer"
                      @click="handleAccountEdit(account.accountID)"
              />
            </div>
            <div v-if="IsUserAnAdmin(Store.state.user) || IsUserABroker(Store.state.user)" class="tw:tooltip tw:tooltip-bottom tw:flex tw:items-center" data-tip="View as Client">
              <ViewIcon :size="16" class="tw:cursor-pointer tw:stroke-capital-blue" @click="handleViewAsClient(account.accountID)" />
            </div>

            <AccountWarning v-if="!isAClientAccount" :account="account" />
          </div>

          <a v-if="showDomain && !isAClientAccount" :href="`//${account.domainName}`" target="_blank" rel="noopener noreferrer" class="tw:lowercase">{{ account.domainName }}</a>
          <span v-if="showDomain && !isAClientAccount" class="tw:text-md tw:text-gray-500"> &nbsp; </span>
          <span v-if="!isAClientAccount" class="tw:text-gray-500 tw:uppercase tw:text-xs"> {{ account.topLevelIndustry }}</span>

          <div v-if="account.parentCompany" class="tw:uppercase tw:text-gray-500 tw:text-xs">
            <strong>Parent Account:&nbsp;&nbsp;</strong>
            <template v-if="canViewParentAccount(account.parentAccountID)">
              <router-link :to="`/parent/account/${account.parentAccountID}/overview`" data-test="parent-company">{{
                account.parentCompany
              }}</router-link>
            </template>
            <span v-else>{{ account.parentCompany }}</span>
          </div>
        </div>

        <div v-if="!isAClientAccount" class="text-right statistics">
          <v-skeleton-loader v-if="loadingKPI && !errorKPI" />
          <v-alert border="top" color="error-lighten-2" v-if="!loadingKPI && errorKPI">{{ errorKPI }}</v-alert>
          <Statistics v-if="!loadingKPI && !errorKPI && !isAClientAccount" dense no-borders :statistics="statistics" />
        </div>
      </div>
    </div>

    <div class="tw:border-t tw:border-b tw:border-l-0 tw:border-r-0 tw:border-gray-200 tw:border-solid">
      <!-- TODO: V-Tabs doesnt pattern match on sub routes (account/:id/programs/1234), using currentTab is a workaround for now -->
      <v-tabs id="account-tabs" show-arrows bg-color="grey-lighten-4"
              class="tab-bar tw:uppercase"
              selected-class="tw:text-primary"
              slider-color="primary"
              :model-value="currentTab"
      >
        <v-tab
          v-for="tab in accountTabs"
          :key="tab.key"
          :ripple="false"
          :value="tab.key"
          class="tw:text-xs tw:text-gray-600"
          :to="tab.route"
        >
          {{ tab.label }}
        </v-tab>
      </v-tabs>
    </div>

    <div
      v-if="!loadingTabs && accountTabs.length === 0"
      class="tw:alert tw:p-4 tw:w-xl tw:mx-auto tw:mt-8"
    >
      <TriangleAlert size="18"/>
      <span>
        Insufficient permissions. Contact <a href="mailto:platform@cacgroup.com">platform@cacgroup.com</a>
        for assistance.
      </span>
    </div>

    <router-view v-if="!loadingAccount && accountTabs.length > 0" :account="account"></router-view>
  </div>
</template>

<script>
import { accountService, userAccountService } from '@/services/api';
import Statistics from '@/components/Statistics';
import { ErrorLoadingKPI, FailedToLoadPage } from '@/utilities/Errors';
import { DateTime } from 'luxon';
import { compactMoney } from '@/utilities/Money';
import { compactNumber } from '@/utilities/Number';
import FavoriteIndicator from '@/components/FavoriteIndicator';
import { Events } from '@/store/EventBus';
import AccountWarning from '@/components/AccountWarning';
import {
  IsUserABroker,
  IsUserAClient,
  IsUserAnAdmin,
  GetAllowedParentAccountIds,
} from '@/utilities/AuthUtils';
import { AccountTabPermissionOrder } from '@/types/UserPermissions';
import { Commit, Store } from '@/store';
import { VSkeletonLoader } from 'vuetify/components/VSkeletonLoader';
import { Pencil, View as ViewIcon, TriangleAlert } from 'lucide-vue-next';
import { StoreMutationType } from '@/types/StoreMutationType';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';

export default {
  name: 'AccountView',
  components: { Statistics, FavoriteIndicator, AccountWarning, VSkeletonLoader, ViewIcon, Pencil, TriangleAlert },
  setup() {
    const { getTabs } = useEntityTabs();

    return {
      getTabs,
    };
  },
  data: () => ({
    loadingAccount: true,
    errorAccount: undefined,
    loadingKPI: true,
    errorKPI: undefined,
    account: {},
    statistics: [],
    favoriteProgress: false,
    accountTabs: [],
    loadingTabs: true,
  }),
  computed: {
    Store() {
      return Store;
    },
    isProspect() {
      return this.account?.accountStatus === 'P';
    },
    showDomain() {
      if (this.account.noDomain === true) {
        return false;
      }
      return this.account.domainName;
    },
    isAClientAccount() {
      return IsUserAClient(Store.state.user);
    },
    currentTab() {
      return this.$route.path.split('/')[3];
    },
  },
  methods: {
    IsUserAnAdmin,
    IsUserABroker,
    async toggleFavorite(account) {
      this.favoriteProgress = true;
      if (!account.favorite) {
        await userAccountService.remove(this.account.accountID);
        this.account.favorite = false;
      } else {
        await userAccountService.create(this.account.accountID);
        this.account.favorite = true;
      }
      this.favoriteProgress = false;
    },
    async loadAccount() {
      try {
        this.loadingAccount = true;
        this.account = await accountService.findById(this.$route.params.id);
        if (!this.account.displayName) this.account.displayName = this.account.accountName;
        return true;
      } catch (err) {
        if ('response' in err && err.response.status === 404) {
          return this.$router.replace('/');
        }
        console.error(err);
        this.errorAccount = FailedToLoadPage;
        return false;
      } finally {
        this.loadingAccount = false;
      }
    },
    async refreshKPI() {
      try {
        this.loadingKPI = true;
        //if user, return early or else kpi will fail w/ 404
        if (IsUserAClient(Store.state.user)) {
          this.loadingKPI = false;
          return;
        }
        const kpi = await accountService.kpi(this.$route.params.id);
        let clientSince = DateTime.fromISO(kpi.clientSince).toLocaleString(DateTime.DATE_SHORT);
        if (clientSince.includes('Invalid')) {
          clientSince = '--';
        }

        this.statistics = [
          {
            key: 'client-since',
            value: clientSince,
            heading: 'Client Since',
            loading: false,
            classes: ['dense', 'd-lg-block'],
          },
          {
            key: 'current-premium',
            value: compactMoney(this.isProspect ? kpi.externalPremium : kpi.currentPremium),
            heading: this.isProspect ? 'External Premium' : 'Current Premium',
            loading: false,
            classes: ['dense'],
            toolTip: 'Includes BOR Premium',
          },
          {
            key: 'policy-count',
            value: compactNumber(this.isProspect ? kpi.externalPolicies : kpi.currentPolicies),
            heading: this.isProspect ? 'External Policies' : 'Current Policies',
            loading: false,
            classes: ['dense'],
          },
        ];
      } catch (err) {
        console.error(err);
        this.errorKPI = ErrorLoadingKPI;
      } finally {
        this.loadingKPI = false;
      }
    },
    handleAccountEdit(accountID) {
      this.$router.push(`/account/${accountID}/edit`);
    },
    async handleViewAsClient(accountID) {
      Commit(StoreMutationType.ENABLE_CLIENT_VIEW_MODE, {
        id: -1,
        name: '',
        allowedAccountIds: [accountID],
      });

      await this.loadAccount();
      await this.refreshKPI();
    },
    canViewParentAccount(accountId) {
      return !this.isAClientAccount || GetAllowedParentAccountIds(Store.state.user).includes(accountId);
    },
    async loadAccountTabs() {
      this.loadingTabs = true;
      try {
        this.accountTabs = await this.getTabs('account', this.account.accountID.toString());
      } catch (error) {
        console.error('Error loading account tabs:', error);
        this.accountTabs = [];
      } finally {
        this.loadingTabs = false;
      }
    },
  },
  watch: {
    async isAClientAccount() {
      // when client view is exited, fetch KPIs and display
      await this.loadAccount();
      await this.refreshKPI();
      await this.loadAccountTabs();
    },
    accountTabs() {
      if (!this.accountTabs.map((t) => t.key).includes(this.currentTab)) {
        if (this.accountTabs.length > 0) {
          this.$router.push(this.accountTabs[0].route);
        }
      }
    }
  },

  // -----------
  // lifecycles
  // -----------
  async created() {
    const loaded = await this.loadAccount();
    if (loaded) {
      await this.refreshKPI();
      await this.loadAccountTabs();
      addEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
    }
  },
  mounted() {
    // Register resizable items
    registerResizeItem({ name: 'account-header', selector: 'account-header' });
    registerResizeItem({ name: 'account-tabs', selector: 'account-tabs' });

    // Determine the landing tab based on permissions
    const user = Store.state.user;
    const initialTab = this.$route.path.split('/').slice(3).join('/') || 'overview';
    let landingTab = initialTab;

    // TODO app-role: if we migrate all roles to db roles, this should be applied to all roles
    if (IsUserAClient(user)) {
      // Find the first tab the user has permission to view, if the current tab is restricted
      if (!user?.permissions.includes(landingTab)) {
        landingTab =
          AccountTabPermissionOrder.find((tabPerm) => user?.permissions.includes(tabPerm.permission))?.tab ||
          initialTab;
      }
    }

    const url = `/account/${this.$route.params.id}${landingTab ? '/' + landingTab : ''}`;
    const navigationOptions = {
      path: url,
      ...(landingTab === initialTab && { query: this.$route.query }), // Preserve query only if the tab hasn't changed
    };

    this.$router.push(navigationOptions);
  },
  beforeUnmount() {
    removeEventListener(Events.REFRESH_ACCOUNT_KPI, this.refreshKPI);
  },
};
</script>

<style scoped>
.progress {
  display: grid;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  height: 10rem;
}
</style>
